import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx'; 
import { 
  Users, Search, Baby, ShieldCheck, 
  ChevronDown, ChevronUp, Phone, UserCheck, 
  FileSpreadsheet, Trash2, AlertCircle
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const BaseDatosKids = () => {
  // 1. Extraemos los datos reales para auditoría en tiempo real
  const { ninos, setNinos } = useGlobal();

  const [busqueda, setBusqueda] = useState('');
  const [ninoExpandido, setNinoExpandido] = useState(null);
  const [filtroEstatus, setFiltroEstatus] = useState('todos');

  // 2. LÓGICA DE FILTRADO DINÁMICA
  const filtrados = useMemo(() => {
    return ninos.filter(n => 
      n.nombre.toLowerCase().includes(busqueda.toLowerCase()) &&
      (filtroEstatus === 'todos' || 
       (filtroEstatus === 'presente' ? n.estatus === 'en_salon' : n.estatus !== 'en_salon'))
    );
  }, [ninos, busqueda, filtroEstatus]);

  // --- FUNCIÓN DE EXPORTACIÓN (Reporte de Seguridad) ---
  const exportarExcel = () => {
    const datosParaExportar = filtrados.map(nino => ({
      'ID REGISTRO': nino.id,
      'NOMBRE COMPLETO': nino.nombre,
      'EDAD': nino.edad,
      'GRUPO / SALÓN': nino.salon,
      'ESTATUS OPERATIVO': nino.estatus === 'en_salon' ? 'PRESENTE' : 'FUERA',
      'ESTRELLAS ACUM.': nino.estrellas || 0,
      'PROTOCOLO MÉDICO': nino.especial ? 'SÍ' : 'NO',
      'ALERTAS REGISTRADAS': nino.alerta || 'Ninguna',
      'TUTORES AUTORIZADOS': nino.tutores?.map(t => `${t.nombre} (${t.tel})`).join(' | ') || 'No registrado'
    }));

    const hoja = XLSX.utils.json_to_sheet(datosParaExportar);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "IgleKids_Audit");
    XLSX.writeFile(libro, `IgleKids_Reporte_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const toggleExpandir = (id) => {
    setNinoExpandido(ninoExpandido === id ? null : id);
  };

  const eliminarRegistro = (id) => {
    if(window.confirm("¿CONFIRMAS ELIMINACIÓN PERMANENTE? Esta acción eliminará el expediente de seguridad y la cadena de custodia del sistema.")) {
      setNinos(prev => prev.filter(n => n.id !== id));
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500 pb-24 px-4 text-left font-sans">
      
      {/* HEADER DE AUDITORÍA */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b-4 border-slate-100 pb-8">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#0A2440] text-white px-6 py-2 rounded-full shadow-lg">
            <ShieldCheck size={18} className="text-amber-400" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] italic">Administración Central</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 italic tracking-tighter uppercase leading-none">Auditoría Kids</h2>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <button 
            onClick={exportarExcel}
            className="flex items-center justify-center gap-3 bg-emerald-500 text-white px-8 py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest hover:bg-emerald-600 transition-all shadow-xl active:scale-95 border-b-4 border-emerald-700"
          >
            <FileSpreadsheet size={20} /> Exportar Reporte
          </button>

          <div className="relative">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" placeholder="Buscar por nombre..." 
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              className="pl-14 pr-6 py-5 bg-white rounded-[2rem] border-2 border-slate-100 shadow-inner focus:border-indigo-500 outline-none w-full md:w-80 font-bold text-slate-700 transition-all"
            />
          </div>
          
          <select 
            value={filtroEstatus} onChange={(e) => setFiltroEstatus(e.target.value)}
            className="bg-white px-8 py-5 rounded-[2rem] border-2 border-slate-100 shadow-sm font-black text-[10px] uppercase tracking-widest text-slate-500 outline-none focus:border-indigo-500 appearance-none"
          >
            <option value="todos">Todos los expedientes</option>
            <option value="presente">Activos en Salón</option>
            <option value="ausente">Fuera de Sistema</option>
          </select>
        </div>
      </div>

      {/* LISTADO DE EXPEDIENTES */}
      <div className="space-y-4">
        {filtrados.length === 0 ? (
          <div className="py-24 text-center bg-slate-50 rounded-[4rem] border-4 border-dashed border-slate-200 opacity-40">
             <Users size={80} strokeWidth={1} className="mx-auto mb-4" />
             <p className="text-xl font-black uppercase tracking-widest italic">No se encontraron registros</p>
          </div>
        ) : (
          filtrados.map(nino => (
            <div key={nino.id} className={`bg-white rounded-[3rem] shadow-sm border-2 transition-all duration-300 ${ninoExpandido === nino.id ? 'border-indigo-500 shadow-2xl scale-[1.01]' : 'border-slate-50 hover:border-slate-200'}`}>
              
              <div 
                onClick={() => toggleExpandir(nino.id)}
                className="p-6 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer"
              >
                <div className="flex items-center gap-8">
                  <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center relative shadow-inner ${nino.estatus === 'en_salon' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    {nino.foto ? <img src={nino.foto} className="w-full h-full object-cover rounded-[2rem]" alt="Perfil" /> : <Baby size={40} />}
                    {nino.estatus === 'en_salon' && <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white animate-pulse" />}
                  </div>
                  <div className="text-left">
                    <h3 className="text-3xl font-black text-slate-800 tracking-tighter uppercase italic">{nino.nombre}</h3>
                    <div className="flex items-center gap-4 mt-2">
                       <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-4 py-1.5 rounded-full uppercase tracking-widest border border-indigo-100">{nino.salon}</span>
                       <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{nino.edad}</span>
                       {nino.especial && <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase italic">Protocolo Activo</span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 md:gap-12">
                  <div className="text-center">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 italic">Puntos de Honor</p>
                    <span className="bg-amber-50 text-amber-600 px-6 py-2 rounded-2xl font-black text-lg border-2 border-amber-100 shadow-sm">{nino.estrellas || 0} ⭐</span>
                  </div>
                  <div className="text-slate-300">
                    {ninoExpandido === nino.id ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
                  </div>
                </div>
              </div>

              {ninoExpandido === nino.id && (
                <div className="border-t-4 border-slate-50 p-10 md:p-14 bg-slate-50/50 animate-in slide-in-from-top-4 rounded-b-[3rem]">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    
                    {/* COLUMNA TUTORES */}
                    <div className="space-y-6 text-left">
                      <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-500 flex items-center gap-2 italic">
                        <Users size={18} /> Cadena de Custodia Autorizada
                      </h4>
                      <div className="grid gap-4">
                        {(nino.tutores || []).map((t, i) => (
                          <div key={i} className="bg-white p-6 rounded-[2.5rem] border-2 border-slate-100 flex items-center justify-between shadow-sm">
                            <div className="flex items-center gap-5">
                              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-400 shadow-inner">
                                <UserCheck size={28} />
                              </div>
                              <div className="text-left">
                                <p className="text-lg font-black text-slate-800 uppercase italic tracking-tighter leading-none">{t.nombre}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Padre / Responsable Legal</p>
                              </div>
                            </div>
                            <a href={`tel:${t.tel}`} className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all active:scale-90">
                              <Phone size={24} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* COLUMNA ACCIONES */}
                    <div className="space-y-6 text-left">
                      <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-rose-500 flex items-center gap-2 italic">
                        <AlertCircle size={18} /> Seguridad Médica & Legal
                      </h4>
                      <div className="bg-white p-8 rounded-[3rem] border-2 border-slate-100 space-y-6 shadow-inner">
                         <div className="space-y-2">
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic leading-none">Observaciones Clínicas:</p>
                           <p className="text-sm font-bold text-slate-700 leading-relaxed uppercase italic">{nino.alerta || 'Sin condiciones médicas registradas.'}</p>
                         </div>
                         
                         <div className="flex gap-4 pt-4">
                           <button className="flex-1 py-4 bg-[#0A2440] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl border-b-4 border-slate-800 active:scale-95 transition-all">Perfil del Padre</button>
                           <button onClick={() => eliminarRegistro(nino.id)} className="p-4 bg-rose-50 text-rose-600 rounded-2xl hover:bg-rose-600 hover:text-white transition-all border-2 border-rose-100 shadow-sm">
                             <Trash2 size={24} />
                           </button>
                         </div>
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default BaseDatosKids;